import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
// LoginPage is kept as an eager import: it's the first paint for logged-out
// users and is small on its own, so lazy-loading it would only add a round
// trip with no bundle-size benefit.
import LoginPage from "@/pages/LoginPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const PurchaseOrdersPage = lazy(() => import("@/pages/PurchaseOrdersPage"));
const PurchaseOrderDetailPage = lazy(() => import("@/pages/PurchaseOrderDetailPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const SalesOrdersPage = lazy(() => import("@/pages/SalesOrdersPage"));
const SalesOrderDetailPage = lazy(() => import("@/pages/SalesOrderDetailPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const PnLReportPage = lazy(() => import("@/pages/PnLReportPage"));
const SupplierPortalPage = lazy(() => import("@/pages/SupplierPortalPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const UserManagementPage = lazy(() => import("@/pages/UserManagementPage"));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));
const PaymentTrackingPage = lazy(() => import("@/pages/PaymentTrackingPage"));
const SalesAnalyticsPage = lazy(() => import("@/pages/SalesAnalyticsPage"));
const SettlementSummaryPage = lazy(() => import("@/pages/SettlementSummaryPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const OutboundExplorerPage = lazy(() => import("@/pages/OutboundExplorerPage"));
const PublicPaymentApprovalPage = lazy(() => import("@/pages/PublicPaymentApprovalPage"));

function PageLoader() {
    return (
        <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
            <Loader2 className="animate-spin text-green-500" size={32} />
        </div>
    );
}

function ProtectedRoutes() {
    const { user, loading } = useAuth();

    if (loading) {
        return <PageLoader />;
    }
    if (!user) return <Navigate to="/login" />;

    return (
        <Layout>
            <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/sales-orders" element={<SalesOrdersPage />} />
                <Route path="/sales-orders/:id" element={<SalesOrderDetailPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/pnl-report" element={<PnLReportPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/payments" element={<PaymentTrackingPage />} />
                <Route path="/sales-analytics" element={<SalesAnalyticsPage />} />
                <Route path="/settlement-summary" element={<SettlementSummaryPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/user-management" element={<UserManagementPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />
                <Route path="/outbound-explorer" element={<OutboundExplorerPage />} />
            </Routes>
        </Layout>
    );
}

function App() {
    useEffect(() => {
        document.documentElement.classList.add("dark");
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster theme="dark" richColors position="top-right" />
                <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/supplier/:token" element={<SupplierPortalPage />} />
                            <Route path="/approve-payment/:token" element={<PublicPaymentApprovalPage />} />
                            <Route path="/*" element={<ProtectedRoutes />} />
                        </Routes>
                    </Suspense>
                </ErrorBoundary>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
